<template>
  <!-- Delete Dialog -->
  <PeopleDeleteDialog
    v-if="person"
    v-model:visibility="deletePeopleDialogVisibility"
    :person="person" />
  <EmployeeDrawer
    v-if="selectedEmployee"
    v-model:visibility="employeeDrawerVisibility"
    :employee="selectedEmployee" />

  <!-- Drawer content -->
  <SmDrawer v-model:visibility="localVisibility" :title="i18n.t('person')">
    <!-- Header -->
    <template #close>
      <div class="header">
        <div
          v-if="person"
          v-require-permission="CompanyAccessRole.ADMIN"
          style="margin-right: 10px; display: flex; align-items: center"
          @click="handleUserDelete(person?.email || '')">
          <Trash class="cursor-pointer hover:text-magenta" :size="20" />
        </div>
        <SmButtonClose
          data-test-id="person-detail-drawer-close-button"
          @click="localVisibility = !localVisibility" />
      </div>
    </template>

    <!-- Drawer Content -->
    <template #default>
      <!-- person Header -->
      <div class="person-header">
        <SmAvatar :name="person?.name || ''" class="bg-gray-200" />
        <div>
          <h3>{{ person?.name }}</h3>
          <p>{{ person?.email }}</p>
        </div>
      </div>

      <!-- Spacer -->
      <div style="height: 1rem" />

      <el-tabs v-model="activeTab">
        <!-- Information Tab -->
        <el-tab-pane :label="capitalize(i18n.t('informations'))" name="first">
          <div class="person_header">
            <div style="width: 100%">
              <PeopleDrawerInformation v-if="person" v-model:person="person" />
            </div>
          </div>
          <div v-if="person" class="linked-employees">
            <div class="linked-employees__header">
              <h3>{{ i18n.t('employees') }}</h3>
              <span
                v-if="person.employee_ids && person.employee_ids.length"
                class="linked-employees__badge">
                {{ person.employee_ids.length }}
              </span>
            </div>
            <div v-if="linkedEmployeesLoading" class="linked-employees__loading">
              {{ i18n.t('loading') }}
            </div>
            <div v-else-if="!person.employee_ids || person.employee_ids.length === 0">
              <span class="no-data">{{ i18n.t('general.noData') }}</span>
            </div>
            <div v-else-if="linkedEmployees.length === 0" class="no-data">
              {{ i18n.t('general.noData') }}
            </div>
            <ul v-else class="linked-employees__list">
              <li
                v-for="employee in linkedEmployees"
                :key="employee._id"
                class="linked-employees__item"
                role="button"
                tabindex="0"
                @click="openEmployeeDrawer(employee)"
                @keydown.enter.prevent="openEmployeeDrawer(employee)"
                @keydown.space.prevent="openEmployeeDrawer(employee)">
                <SmAvatar
                  :name="employee.name || employee.email"
                  class="linked-employees__avatar" />
                <div class="linked-employees__meta">
                  <p class="linked-employees__name">
                    {{ employee.name || employee.email }}
                  </p>
                  <p class="linked-employees__email">{{ employee.email }}</p>
                  <p v-if="employee.position" class="linked-employees__position">
                    {{ employee.position }}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </el-tab-pane>

        <!-- Licenses Tab -->
        <el-tab-pane
          :label="i18n.t('account', { count: 2 })"
          name="second"
          class="deep-contain-none-important"
          lazy>
          <AccountTable
            v-if="person"
            :softwares="applications"
            display-application-name
            :filter="filter"
            :display-email="false"
            display-price
            display-sub-account
            :display-status="false"
            :display-last-logged-in="false"
            display-status-indicator
            @refetch-users="
              () => {
                emit('refetchLicences')
              }
            " />
        </el-tab-pane>

        <!-- Statistics Tab -->
        <el-tab-pane :label="i18n.t('statistics')" name="third" lazy>
          <PeopleDrawerStatistics
            v-if="licenses"
            :licenses="licenses"
            :application-costs="applicationCosts" />
        </el-tab-pane>

        <!-- Aliases Tab -->
        <el-tab-pane label="Aliases" name="fourth" class="h-full" lazy>
          <PeopleDrawerAliases
            v-if="person"
            v-model:person="person"
            :applications="applications" />
        </el-tab-pane>
      </el-tabs>
    </template>
  </SmDrawer>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    CompanyAccessRole,
    Employee,
    EmployeesService,
    SoftwareOut,
    UsersService,
  } from '@/client'

  import { computedAsync } from '@vueuse/core'
  import { AccountStore } from '@/stores/account/account.store'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { AccountFilters } from '@/stores/account/account.filter'
  import { capitalize } from '@/utils/formatter/formatter'
  import { EnrichedAccount, PersonStore } from '@/stores/person/person.store'
  import { Trash } from 'lucide-vue-next'
  import PeopleDeleteDialog from './PeopleDeleteDialog.vue'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { logError } from '@/utils/errors'
  import EmployeeDrawer from '@/components/discovery/EmployeeDrawer.vue'

  const i18n = useI18n()

  const activeTab = ref('first')

  const props = defineProps({
    personId: {
      type: String,
      required: true,
    },
    drawerVisibility: {
      type: Boolean,
      required: true,
    },
  })

  const emit = defineEmits([
    'update:drawerVisibility',
    'refetchLicences',
    'close',
  ])

  const applications = ApplicationStore.useApplications<SoftwareOut>().data

  const deletePeopleDialogVisibility = ref<boolean>(false)
  const deletePeopleDialogEmail = ref<string>('')

  const employeeDrawerVisibility = ref<boolean>(false)
  const selectedEmployee = ref<Employee | null>(null)

  const localVisibility = computed({
    get: () => props.drawerVisibility,
    set: (value) => {
      emit('update:drawerVisibility', value)
      emit('close')
    },
  })

  const { data, updateParameters } = PersonStore.usePersons<EnrichedAccount>(
    { id: props.personId },
    undefined,
    undefined,
    true
  )

  // Filter
  const filter = ref<AccountFilters>({})

  watch(
    () => props.personId,
    async () => {
      filter.value.accountId = props.personId
      updateParameters({ id: props.personId })
      selectedEmployee.value = null
      employeeDrawerVisibility.value = false
    },
    { immediate: true }
  )

  const person = computed(() => {
    return data.value[0]
  })

  const linkedEmployees = ref<Employee[]>([])
  const linkedEmployeesLoading = ref(false)

  const loadLinkedEmployees = async () => {
    const employeeIds = person.value?.employee_ids ?? []
    if (!employeeIds.length) {
      linkedEmployees.value = []
      return
    }

    linkedEmployeesLoading.value = true

    try {
      const employees = await Promise.all(
        employeeIds.map(async (employeeId) => {
          try {
            return await EmployeesService.getEmployeeApiV1DataSourceEmployeesEmployeeIdGet(
              {
                employeeId,
                includeDeleted: true,
              }
            )
          } catch (error) {
            logError(error, 'Failed to load linked employee', { employeeId })
            return null
          }
        })
      )

      linkedEmployees.value = employees.filter(
        (employee): employee is Employee => Boolean(employee)
      )
    } finally {
      linkedEmployeesLoading.value = false
    }
  }

  const openEmployeeDrawer = (employee: Employee) => {
    selectedEmployee.value = employee
    employeeDrawerVisibility.value = true
  }

  // necessary for delete person
  watch(
    () => data.value,
    (newValue) => {
      if (newValue && Object.keys(newValue).length === 0) {
        localVisibility.value = false
      }
    }
  )

  watch(
    () => person.value?.employee_ids?.join(',') ?? '',
    async () => {
      await loadLinkedEmployees()
    },
    { immediate: true }
  )

  const handleUserDelete = (email: string) => {
    deletePeopleDialogEmail.value = email
    deletePeopleDialogVisibility.value = true
  }

  // Statistic data
  const licenses = computedAsync(async () => {
    if (!person.value?._id) return []
    return await AccountStore.getAccounts({ accountId: person.value?._id })
  })

  // Software cost
  const applicationCosts = ref<number>(0)

  watch(
    () => person.value?._id,
    async () => {
      const preferenceStore = usePreferenceStore()
      if (!person.value?._id) return
      applicationCosts.value =
        await UsersService.getUserCostApiV1ManagementUsersEmailOrIdCostGet({
          emailOrId: person.value?._id,
          targetCurrencyCode: preferenceStore.preferences?.currency_code,
        })
    }
  )
</script>

<style scoped>
  .person_header {
    display: flex;
    flex-direction: row;
  }

  .person-header {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  .linked-employees {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .linked-employees__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .linked-employees__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.75rem;
    height: 1.75rem;
    padding: 0 0.5rem;
    border-radius: 9999px;
    background: theme('colors.gray[100]');
    color: theme('colors.gray[600]');
    font-size: 0.9rem;
    font-weight: 600;
  }

  .linked-employees__loading,
  .no-data {
    color: theme('colors.gray[500]');
    font-size: 0.95rem;
  }

  .linked-employees__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .linked-employees__item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid theme('colors.gray[200]');
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: theme('colors.gray[300]');
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
      outline: none;
    }
  }

  .linked-employees__avatar {
    width: 40px;
    height: 40px;
  }

  .linked-employees__meta p {
    margin: 0;
  }

  .linked-employees__name {
    font-weight: 600;
  }

  .linked-employees__email {
    color: theme('colors.gray[500]');
    font-size: 0.875rem;
  }

  .linked-employees__position {
    color: theme('colors.gray[400]');
    font-size: 0.8125rem;
  }
</style>
<style lang="css">
  .deep-contain-none-important * {
    contain: none !important;
  }
</style>
